import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import {
  Gift, CopySimple, ShareNetwork, WhatsappLogo, EnvelopeSimple,
  CheckCircle, Sparkle, ArrowRight, Users,
} from "@phosphor-icons/react";

function Stat({ label, value, accent }) {
  return (
    <div className="border border-[#E5E7E0] bg-white p-6">
      <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A65]">{label}</div>
      <div
        className={`mt-3 font-editorial text-4xl tracking-tight ${
          accent ? "text-[#7C8B6E]" : "text-[#1C1C1A]"
        }`}
        data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {value}
      </div>
    </div>
  );
}

export default function Referrals() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data: d } = await api.get("/referrals/me");
        if (on) setData(d);
      } catch (e) {
        toast.error("Impossibile caricare il programma inviti");
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, []);

  const copy = async (text, label = "Copiato") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      // Fallback: select + execCommand
      try {
        const ta = document.createElement("textarea");
        ta.value = text; document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        toast.success(label);
      } catch {
        toast.error("Copia non riuscita — copia il testo manualmente");
      }
    }
  };

  if (loading) {
    return (
      <AppShell title="Inviti">
        <div className="text-[#6B6A65]" data-testid="referrals-loading">Caricamento…</div>
      </AppShell>
    );
  }

  const link = data?.link || "";
  const code = data?.code || "";
  const total = data?.invites_total ?? 0;
  const converted = data?.invites_converted ?? 0;
  const creditsEarned = data?.credits_earned_total ?? 0;
  const rewardReferrer = data?.reward_credits_per_conversion ?? 30;
  const rewardInvitee = data?.reward_credits_invitee_welcome ?? 30;

  // iter 309 r4: copy aggiornata ai crediti (no più "30 giorni Pro").
  const waMsg = encodeURIComponent(
    `Ciao! Volevo passarti Pronto. È un'app che uso per gestire annunci, foto, brochure e contatti — fa risparmiare un sacco di tempo, soprattutto dopo i sopralluoghi. Iscriviti col mio link e ti regalano ${rewardInvitee} crediti per iniziare: ${link}`
  );
  const emailSubject = encodeURIComponent("Ti consiglio Pronto");
  const emailBody = encodeURIComponent(
    `Ciao,\n\nti volevo passare Pronto: è l'app che uso per gestire annunci, foto, brochure e contatti.\nDopo i sopralluoghi è una manna — fa risparmiare un sacco di tempo.\n\nIscriviti col mio link e ti regalano ${rewardInvitee} crediti gratuiti per provarla:\n${link}\n\nFammi sapere cosa ne pensi.`
  );

  return (
    <AppShell title="Inviti" breadcrumb="Inviti">
        {/* Hero */}
        <div className="flex items-start gap-4 mb-10">
          <div className="hidden sm:flex w-14 h-14 items-center justify-center bg-[#7C8B6E] text-[#FFFFFF]">
            <Gift size={26} weight="duotone" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B6A65] mb-2">
              Programma inviti Pronto
            </p>
            <h1
              className="font-editorial text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-[#1C1C1A]"
              data-testid="referrals-title"
            >
              Invita un agente.<br/>
              <span className="italic-accent text-[#7C8B6E]">{rewardReferrer} crediti</span> per te.
            </h1>
            <p className="mt-4 text-[#6B6A65] max-w-xl leading-relaxed">
              Per ogni collega che si iscrive col tuo link e diventa Pro, ricevi{" "}
              <strong className="text-[#1C1C1A]">{rewardReferrer} crediti gratuiti</strong>, immediati.
              Lui riceve subito <strong className="text-[#1C1C1A]">{rewardInvitee} crediti</strong> di benvenuto.
              I crediti si usano su foto, brochure, social — non scadono.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
          <Stat label="Inviti" value={total} />
          <Stat label="Convertiti in Pro" value={converted} />
          <Stat label="Crediti guadagnati" value={creditsEarned} accent />
        </div>

        {/* Link card */}
        <div className="border border-[#E5E7E0] bg-white p-6 lg:p-8 mb-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6B6A65] mb-4">
            <Sparkle size={12} weight="fill" className="text-[#7C8B6E]" />
            Il tuo link personale
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
            <input
              type="text"
              readOnly
              value={link}
              onFocus={(e) => e.target.select()}
              className="flex-1 px-4 py-3 bg-[#F1F4ED] border border-transparent text-[#1C1C1A] font-mono text-sm focus:outline-none focus:border-[#7C8B6E]"
              data-testid="referral-link-input"
            />
            <button
              onClick={() => copy(link, "Link copiato")}
              className="btn-primary justify-center px-5 sm:min-w-[140px]"
              data-testid="copy-link-btn"
            >
              <CopySimple size={16} weight="bold" /> Copia link
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
            <span className="text-[#6B6A65]">o condividi il codice</span>
            <code
              className="px-3 py-1 bg-[#7C8B6E] text-[#FFFFFF] tracking-[0.3em] text-sm font-medium"
              data-testid="referral-code"
            >
              {code}
            </code>
            <button
              onClick={() => copy(code, "Codice copiato")}
              className="text-[#7C8B6E] hover:text-[#7C8B6E] underline"
              data-testid="copy-code-btn"
            >
              copia
            </button>
          </div>

          {/* Share row */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <a
              href={`https://wa.me/?text=${waMsg}`}
              target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 border border-[#E5E7E0] hover:border-[#7C8B6E] text-sm transition-colors"
              data-testid="share-whatsapp-btn"
            >
              <WhatsappLogo size={18} weight="duotone" className="text-[#7C8B6E]" /> WhatsApp
            </a>
            <a
              href={`mailto:?subject=${emailSubject}&body=${emailBody}`}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-[#E5E7E0] hover:border-[#7C8B6E] text-sm transition-colors"
              data-testid="share-email-btn"
            >
              <EnvelopeSimple size={18} weight="duotone" className="text-[#7C8B6E]" /> Email
            </a>
            <button
              onClick={async () => {
                if (navigator.share) {
                  try {
                    await navigator.share({
                      title: "Pronto",
                      text: "Annunci immobiliari in 60 secondi",
                      url: link,
                    });
                  } catch { /* user cancelled */ }
                } else {
                  copy(link, "Link copiato");
                }
              }}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-[#E5E7E0] hover:border-[#7C8B6E] text-sm transition-colors"
              data-testid="share-system-btn"
            >
              <ShareNetwork size={18} weight="duotone" className="text-[#7C8B6E]" /> Condividi
            </button>
            <button
              onClick={() => copy(link, "Link copiato")}
              className="flex items-center justify-center gap-2 px-4 py-3 border border-[#E5E7E0] hover:border-[#7C8B6E] text-sm transition-colors"
              data-testid="share-copy-btn"
            >
              <CopySimple size={18} weight="duotone" className="text-[#7C8B6E]" /> Copia
            </button>
          </div>
        </div>

        {/* How it works */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {[
            { n: "1", t: "Condividi il link", d: "WhatsApp, email o passaparola al tuo collega di agenzia." },
            { n: "2", t: "Si iscrive gratuitamente", d: `Vede chi l'ha invitato e riceve subito ${rewardInvitee} crediti di benvenuto.` },
            { n: "3", t: `Diventa Pro → +${rewardReferrer} crediti`, d: `Quando passa al piano Pro, ti aggiungiamo ${rewardReferrer} crediti gratuiti sul tuo account.` },
          ].map((s) => (
            <div key={s.n} className="border border-[#E5E7E0] bg-white p-5">
              <div className="font-editorial text-3xl text-[#7C8B6E] mb-2">{s.n}</div>
              <div className="font-medium text-[#1C1C1A] mb-1">{s.t}</div>
              <div className="text-sm text-[#6B6A65] leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>

        {/* Invitees list */}
        <div className="border border-[#E5E7E0] bg-white">
          <div className="px-6 py-4 border-b border-[#E5E7E0] flex items-center gap-2">
            <Users size={18} weight="duotone" className="text-[#7C8B6E]" />
            <h2 className="font-editorial text-xl tracking-tight">I tuoi invitati</h2>
            <span className="ml-auto text-xs uppercase tracking-[0.2em] text-[#6B6A65]">
              {data?.invitees?.length || 0} {data?.invitees?.length === 1 ? "agente" : "agenti"}
            </span>
          </div>
          {!data?.invitees?.length ? (
            <div className="px-6 py-10 text-center text-[#6B6A65]" data-testid="invitees-empty">
              Nessun invitato per ora. Condividi il link sopra per iniziare.
            </div>
          ) : (
            <ul className="divide-y divide-[#E5E7E0]" data-testid="invitees-list">
              {data.invitees.map((inv, idx) => (
                <li
                  key={idx}
                  className="px-6 py-4 flex items-center gap-4"
                  data-testid={`invitee-row-${idx}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#F1F4ED] flex items-center justify-center text-sm font-medium text-[#7C8B6E]">
                    {(inv.name || "?").split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#1C1C1A] font-medium truncate">{inv.name}</div>
                    <div className="text-xs text-[#6B6A65] font-mono truncate">{inv.email_masked}</div>
                  </div>
                  {inv.status === "pro" ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-[#7C8B6E] text-[#FFFFFF] uppercase tracking-widest">
                      <CheckCircle size={12} weight="fill" className="text-[#F1F4ED]" /> Pro · +{rewardReferrer} cr.
                    </span>
                  ) : (
                    <span className="text-xs px-2.5 py-1 border border-[#E5E7E0] text-[#6B6A65] uppercase tracking-widest">
                      Iscritto
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Bottom CTA — back to dashboard */}
        <button
          onClick={() => nav("/dashboard")}
          className="mt-10 inline-flex items-center gap-2 text-sm text-[#7C8B6E] hover:text-[#7C8B6E]"
          data-testid="back-to-dashboard-btn"
        >
          Torna agli immobili <ArrowRight size={14} weight="bold" />
        </button>
      </AppShell>
  );
}
